/**
 * InputReceiver Component
 *
 * A component that connects a GameObject to the Input System.
 * When attached and enabled, it activates the specified action maps
 * from the referenced InputAsset.
 *
 * This component serves as the bridge between the ECS and the Input System,
 * allowing GameObjects to receive input events through their InputAsset configuration.
 */

import {
  component,
  displayName,
  icon,
  color,
  category,
  executeInEditor,
  customEditor,
  field,
} from '@engine/core';

/**
 * InputReceiver — connects a GameObject to the Input System.
 *
 * @example
 * ```typescript
 * const player = createGameObject('Player');
 * player.addComponent(InputReceiver, {
 *   inputAsset: 'assets/player.input.json',
 *   defaultActionMaps: ['Gameplay'],
 *   activateOnEnable: true,
 * });
 * ```
 */
@component('input.InputReceiver')
@customEditor('InputReceiverInspector')
@displayName('Input Receiver')
@icon('Gamepad2')
@color('#e17055')
@category('Input')
@executeInEditor(false)
export class InputReceiver {
  /**
   * Reference to the InputAsset that defines this object's input configuration.
   */
  @field({
    type: 'asset',
    assetType: 'input',
    label: 'Input Asset',
    tooltip: 'The Input Asset containing action maps and bindings',
  })
  inputAsset: string | null = null;

  /**
   * Names of action maps to activate when the component is enabled.
   */
  @field({
    type: 'array',
    elementType: 'string',
    label: 'Default Action Maps',
    tooltip: 'Action maps to activate when this component is enabled',
  })
  defaultActionMaps: string[] = [];

  /**
   * Whether to automatically activate the default action maps
   * when the component becomes enabled.
   */
  @field({
    type: 'bool',
    label: 'Activate on Enable',
    tooltip: 'Automatically activate default action maps when enabled',
  })
  activateOnEnable: boolean = true;

  /**
   * Player index for local multiplayer.
   * -1 = Any player (single player mode)
   * 0+ = Specific player index
   */
  @field({
    type: 'i32',
    min: -1,
    max: 7,
    label: 'Player Index',
    tooltip: 'Player index for local multiplayer (-1 for any player)',
    group: 'Multiplayer',
  })
  playerIndex: number = -1;

  /**
   * Whether this receiver should block input from reaching other receivers
   * with lower priority. Useful for modal UI elements.
   */
  @field({
    type: 'bool',
    label: 'Exclusive',
    tooltip: 'Block input from reaching lower priority receivers',
    group: 'Advanced',
  })
  exclusive: boolean = false;

  /**
   * Priority for input event routing.
   * Higher priority receivers get input first.
   */
  @field({
    type: 'i32',
    min: -1000,
    max: 1000,
    label: 'Priority',
    tooltip: 'Higher priority receives input first (used with exclusive mode)',
    group: 'Advanced',
  })
  priority: number = 0;
}
